import { pickAccountStep } from "./accountSearch";
import { labelResponse, proposeAjeFromAmount } from "./responses";
import { gifiFor } from "../../../../shared/helpers/constants";

// One dispatcher for every cascade type the registry uses. Each case is the
// data-driven equivalent of what used to live inside an applyXxxCascades
// function as an imperative `if (resolved.id === ...)` block.
//
// Adding a new cascade = add an entry to the registry. Adding a new SHAPE
// of cascade = add a case here.
//
// AJE proposals (proposeAjeFromPeerStep, amountThenAje): cascade entries
// supply canonical codes only. Dispatcher derives debitGifi/creditGifi via
// gifiFor() so (canonical, GIFI) mismatches are structurally impossible.

function enrichProposalWithGifi(proposal) {
	let enriched = { ...proposal };
	if (proposal.debitCanonicalCode) enriched.debitGifi = gifiFor(proposal.debitCanonicalCode);
	if (proposal.creditCanonicalCode) enriched.creditGifi = gifiFor(proposal.creditCanonicalCode);
	return enriched;
}

export function dispatch(resolved, ctx, config) {
	if (!config) return resolved;
	let type = config.type;

	// ── Set the step to completed with one of a fixed set of labels ────
	// Used by every "determine_X" cascade and by acknowledgement branches.
	if (type === "labelResponse") {
		let response = ctx.userResponses[config.from || resolved.id];
		if (!response) return resolved;
		return labelResponse(resolved, response, config.labels);
	}

	// ── Set the step to completed with a fixed detail ──────────────────
	if (type === "acknowledge") {
		return { ...resolved, status: "completed", detail: config.detail };
	}

	// ── Set the step to not_applicable with a fixed detail ─────────────
	if (type === "notApplicable") {
		return { ...resolved, status: "not_applicable", detail: config.detail };
	}

	// ── Pick one of several sub-cascades based on a prior user response ─
	// config.of names the userResponses key whose value we branch on.
	if (type === "branch") {
		let response = ctx.userResponses && ctx.userResponses[config.of];
		if (!response) return resolved;
		let groups = config.when || {};
		for (let key of Object.keys(groups)) {
			// keys may be comma-separated lists, e.g. "A,B,C" for "any of A/B/C"
			let matches = key.split(",").map(function (s) { return s.trim(); });
			if (matches.indexOf(response) >= 0) {
				let sub = groups[key];
				// Sub-config may reference the response (for ?: lookups inside its config)
				return dispatch(resolved, { ...ctx, _branchResponse: response }, sub);
			}
		}
		return resolved;
	}

	// ── Walk the chart of accounts and propose / wait / show options ───
	if (type === "pickAccount") {
		return pickAccountStep(resolved, ctx.userResponses[config.responseKey || resolved.id], {
			chartOfAccounts: ctx.chartOfAccounts,
			keywords: config.keywords,
			target: config.target,
			question: config.question,
			foundNoun: config.foundNoun,
			allDetail: config.allDetail,
			blockedDetail: config.blockedDetail,
		});
	}

	// ── User-entered amount → AJE proposal ─────────────────────────────
	if (type === "amountThenAje") {
		return proposeAjeFromAmount(resolved, ctx.userResponses[config.amountKey], {
			proposal: enrichProposalWithGifi(config.proposal),
			promptDetail: config.promptDetail,
			promptQuestion: config.promptQuestion,
			inputField: config.inputField,
		});
	}

	// ── Amount comes from a peer step's data, not from user input ──────
	// peerStepId    — the step.id whose data.amountField we read
	// amountField   — path within step.data (e.g., "balance", "overshoot", "nbv")
	// abs           — if true, take Math.abs of the value (default true)
	// proposal      — debit/creditCanonicalCode + descriptions; GIFI derived
	if (type === "proposeAjeFromPeerStep") {
		let peer = ctx.steps.find(function (s) { return s.id === config.peerStepId; });
		let raw = peer && peer.data ? peer.data[config.amountField] : 0;
		let amount = config.abs === false ? (raw || 0) : Math.abs(raw || 0);
		return {
			...resolved, status: "ready_to_propose", detail: null,
			inline_proposal: { amount: amount, ...enrichProposalWithGifi(config.proposal) },
		};
	}

	// ── A bare inline_context_change proposal ──────────────────────────
	if (type === "inlineContextChange") {
		return { ...resolved, status: "ready_to_propose", detail: null, inline_context_change: config.change };
	}

	// ── A bare inline_mapping proposal (no account lookup needed) ──────
	if (type === "inlineMapping") {
		return { ...resolved, status: "ready_to_propose", detail: null, inline_mapping: config.mapping };
	}

	// ── Escape hatch for cascades that don't fit any pattern ───────────
	// config.resolve(resolved, ctx) -> step. Used for BS_01, EQ_01 share
	// capital, CA_04 orphaned (parses $ from detail string), TXN_CAP_01
	// per-item, and the handful of CA_01 peer-step + math cascades.
	//
	// Same GIFI-from-canonical enforcement as the data-driven types:
	// any inline_proposal / inline_proposals returned by the resolver gets
	// post-processed so debit/creditGifi is derived via gifiFor(). Custom
	// resolvers can specify whatever GIFI they like — it's overwritten.
	if (type === "custom") {
		let result = config.resolve(resolved, ctx);
		if (!result) return result;
		if (result.inline_proposal && (result.inline_proposal.debitCanonicalCode || result.inline_proposal.creditCanonicalCode)) {
			result = { ...result, inline_proposal: enrichProposalWithGifi(result.inline_proposal) };
		}
		if (Array.isArray(result.inline_proposals)) {
			result = { ...result, inline_proposals: result.inline_proposals.map(enrichProposalWithGifi) };
		}
		return result;
	}

	return resolved;
}
